import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatError } from "@/lib/api";
import { Heart, ShieldCheck } from "lucide-react";
import { BRAND } from "@/lib/brand";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [emp, setEmp] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(emp.trim(), pwd);
      toast.success(`Welcome, ${u.name}`);
      nav(u.role === "admin" ? "/admin" : "/mr", { replace: true });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      {/* Left visual */}
      <div className="hidden md:flex relative bg-brand-900 text-white p-12 flex-col justify-between overflow-hidden">
        {/* Crimson depth wash */}
        <div className="absolute inset-0 [background:radial-gradient(circle_at_25%_15%,hsl(var(--brand-600)/0.55),transparent_60%),radial-gradient(circle_at_80%_85%,hsl(var(--brand-950)/0.8),transparent_55%)]"></div>

        {/* ECG heartbeat trace */}
        <svg
          className="absolute inset-x-0 bottom-16 w-full h-28 opacity-[0.16]"
          viewBox="0 0 800 200" fill="none" preserveAspectRatio="none" aria-hidden="true"
        >
          <path
            d="M0 100 H150 L175 100 L195 45 L215 155 L235 100 L260 100 H340 L360 100 L380 70 L400 130 L420 100 H520 L545 100 L565 40 L585 160 L605 100 L630 100 H800"
            stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          />
        </svg>

        <div className="relative">
          <div className="flex items-center gap-4">
            <img src={BRAND.logo} alt={BRAND.companyName} className="h-14 w-auto max-w-[200px] rounded-xl object-contain bg-white p-1.5" />
            <div>
              <div className="font-display font-bold tracking-tight text-lg">{BRAND.companyName}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-brand-200">{BRAND.productName}</div>
            </div>
          </div>
        </div>

        <div className="relative max-w-md space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-xs font-semibold uppercase tracking-[0.15em] ring-1 ring-white/20">
            <Heart className="w-3.5 h-3.5 fill-current text-brand-300" />
            {BRAND.eventDate}
          </div>
          <h1 className="font-display text-4xl font-extrabold leading-tight">
            Personalized
            <span className="block text-brand-300">{BRAND.eventName}</span>
            posters in under a minute.
          </h1>
          <p className="text-brand-100/80 leading-relaxed">{BRAND.tagline}</p>
          <div className="flex flex-wrap gap-3 pt-2">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-xs"><ShieldCheck className="w-3.5 h-3.5"/> Secure login</span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-xs"><Heart className="w-3.5 h-3.5 text-brand-300"/> Brand consistent</span>
          </div>
        </div>

        <div className="relative text-xs text-brand-200/70">© 2026 {BRAND.companyName}.</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-7" data-testid="login-form">

          {/* Mobile header */}
          <div className="md:hidden flex items-center gap-3">
            <img src={BRAND.logo} alt={BRAND.companyShort} className="h-10 w-auto max-w-[150px] object-contain" />
            <div className="font-display font-bold text-brand-ink">{`${BRAND.companyShort} ${BRAND.productName}`}</div>
          </div>

          {/* Desktop logo above form */}
          <div className="hidden md:flex justify-center">
            <img src={BRAND.logo} alt={BRAND.companyName} className="h-20 w-auto max-w-[280px] object-contain" />
          </div>

          <div className="space-y-2">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600">Sign in</div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-brand-ink">Welcome back</h2>
            <p className="text-sm text-slate-500">Use your EMP Code and password to continue.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="emp" className="text-xs font-semibold uppercase tracking-wider text-slate-600">EMP Code</Label>
              <Input
                id="emp" data-testid="login-emp-input"
                placeholder="Enter your EMP code"
                value={emp} onChange={(e) => setEmp(e.target.value)}
                required autoFocus
                className="h-12 bg-brand-50/40 border-brand-200 focus-visible:ring-brand-600"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pwd" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password</Label>
              <Input
                id="pwd" data-testid="login-password-input"
                type="password" placeholder="Enter your password"
                value={pwd} onChange={(e) => setPwd(e.target.value)}
                required
                className="h-12 bg-brand-50/40 border-brand-200 focus-visible:ring-brand-600"
              />
            </div>
          </div>

          <Button
            type="submit" disabled={busy}
            data-testid="login-submit-button"
            className="w-full h-12 bg-brand-600 hover:bg-brand-700 text-white font-semibold shadow-lg shadow-brand-600/20 transition-colors"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>

        </form>
      </div>
    </div>
  );
}
